import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchPropertyCommissions,
  addPropertyCommission,
  updatePropertyCommission,
  deletePropertyCommission,
  fetchEffectivePropertyCommission,
  fetchHeldPropertyCommissions,
  resolveHeldPropertyCommission,
  recordCommissionRefund,
  clearError,
} from "../../redux/slices/propertyCommissionSlice";
import { fetchProperties } from "../../redux/slices/propertySlice";
import { fetchPropertyTypes } from "../../redux/slices/propertyTypeSlice";
import MainLayout from "../layoutpage/MainLayout";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import CommissionRefundModal, { canRecordCommissionRefund } from "./CommissionRefundModal";
import { SUPERADMIN_ONLY_LABEL } from "../../constants/roles";
import { isSuperadminUser } from "../../utils/leadershipRoles";
import { useCurrentUser } from "../../utilities/currentUser";

const PropertyCommissionPage = ({ user, onLogout }) => {
  const dispatch = useDispatch();
  const { user: currentUser } = useCurrentUser();
  // Admin may view rates and held commissions; changing rates, resolving holds and refunds are Superadmin-only.
  const canEdit = isSuperadminUser(currentUser);
  const superadminTitle = canEdit ? undefined : SUPERADMIN_ONLY_LABEL;
  const { list: commissions, status, error } = useSelector(
    (state) => state.propertyCommissions
  );
  const { effective, effectiveStatus, held, heldStatus, heldMeta } = useSelector((state) => state.propertyCommissions);
  
  const properties = useSelector((state) => state.property?.list || []);
  const propertyTypes = useSelector((state) => state.propertyTypes?.items || []);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    commissionable_type: "App\\Models\\Property",
    commissionable_id: "",
    payment_type: "",
    commission_type: "percentage",
    commission_value: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [previewPropertyId, setPreviewPropertyId] = useState("");
  const [resolution, setResolution] = useState(null);
  const [resolutionRate, setResolutionRate] = useState("");
  const [resolutionReason, setResolutionReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [heldFilter, setHeldFilter] = useState("held");
  const [refundSnapshot, setRefundSnapshot] = useState(null);
  const [refundSaving, setRefundSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchPropertyCommissions());
    dispatch(fetchProperties());
    dispatch(fetchPropertyTypes());
    dispatch(fetchHeldPropertyCommissions({ page: 1, status: "held" }));
  }, [dispatch]);

  useEffect(() => {
    if (previewPropertyId) dispatch(fetchEffectivePropertyCommission(previewPropertyId));
  }, [dispatch, previewPropertyId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const openAddModal = () => {
    setFormData({
      commissionable_type: "App\\Models\\Property",
      commissionable_id: "",
      payment_type: "",
      commission_type: "percentage",
      commission_value: "",
    });
    setSelectedItem(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setFormData({
      commissionable_type: item.commissionable_type,
      commissionable_id: item.commissionable_id,
      payment_type: item.payment_type,
      commission_type: "percentage",
      commission_value: item.commission_type === "percentage" ? item.commission_value : "",
    });
    setSelectedItem(item);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const nextData = { ...prev, [name]: value };
      if (name === "commissionable_type") {
        nextData.commissionable_id = ""; // Reset ID when type changes
      }
      return nextData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.commissionable_type || !formData.payment_type || formData.commission_value === "") {
      toast.error("Please fill in all required fields!");
      return;
    }

    const targetId = formData.commissionable_type === "App\\Models\\GreenlandCommissionDefault"
      ? (formData.commissionable_id || user?.company_id || user?.company?.id || commissions.find((row) => row.commissionable_type === formData.commissionable_type)?.commissionable_id)
      : formData.commissionable_id;
    if (!targetId && formData.commissionable_type !== "App\\Models\\GreenlandCommissionDefault") {
      toast.error("The Greenland company ID is unavailable. Reload your admin session and try again.");
      return;
    }

    const value = Number(formData.commission_value);
    if (!Number.isFinite(value) || value < 0 || value > 100 || !/^\d+(\.\d{1,2})?$/.test(String(formData.commission_value))) {
      toast.error("Commission percentage must be between 0 and 100, with up to two decimal places.");
      return;
    }

    try {
      setSaving(true);
      const ruleData = { ...formData, commissionable_id: targetId, commission_type: "percentage", commission_value: value };
      if (isEditing && selectedItem) {
        await dispatch(
          updatePropertyCommission({
            id: selectedItem.id,
            data: { commission_type: "percentage", commission_value: value },
          })
        ).unwrap();
        setSuccessMessage("Commission rule updated successfully!");
      } else {
        await dispatch(addPropertyCommission(ruleData)).unwrap();
        setSuccessMessage("Commission rule created successfully!");
      }
      
      setShowModal(false);
      setShowSuccessModal(true);
      dispatch(fetchPropertyCommissions());
      if (previewPropertyId) dispatch(fetchEffectivePropertyCommission(previewPropertyId));
    } catch (err) {
      toast.error(err || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await dispatch(deletePropertyCommission(itemToDelete.id)).unwrap();
      setShowDeleteModal(false);
      setItemToDelete(null);
      setSuccessMessage("Commission rule deleted successfully!");
      setShowSuccessModal(true);
    } catch (err) {
      toast.error(err || "Failed to delete commission rule");
    }
  };

  const getTargetName = (type, id) => {
    if (type === "App\\Models\\GreenlandCommissionDefault") return "Greenland-wide default";
    if (type === "App\\Models\\Property") {
      const prop = properties.find(p => p.id === parseInt(id));
      return prop ? (prop.title || prop.name) : `Property #${id}`;
    } else {
      const pt = propertyTypes.find(p => p.id === parseInt(id));
      return pt ? (pt.name || pt.title) : `Property Type #${id}`;
    }
  };

  const formatPaymentType = (type) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatSource = (source) => ({
    property: "Property override",
    property_override: "Property override",
    property_type: "Property type default",
    property_type_override: "Property type default",
    company: "Greenland-wide default",
    company_default: "Greenland-wide default",
    greenland_default: "Greenland-wide default",
  }[source] || source || "Configured rule");

  const openAddForType = (type) => {
    setFormData({ commissionable_type: type, commissionable_id: "", payment_type: "", commission_type: "percentage", commission_value: "" });
    setSelectedItem(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleResolve = async (e) => {
    e.preventDefault();
    if (!resolution || resolutionRate === "" || !resolutionReason.trim()) return;
    const rate = Number(resolutionRate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100 || !/^\d+(\.\d{1,2})?$/.test(String(resolutionRate))) {
      toast.error("Commission percentage must be between 0 and 100, with up to two decimal places.");
      return;
    }
    try {
      setResolving(true);
      await dispatch(resolveHeldPropertyCommission({ id: resolution.id, commission_value: rate, reason: resolutionReason.trim() })).unwrap();
      toast.success("Held commission resolved. Its payment-time rate is now recorded.");
      setResolution(null);
      setResolutionRate("");
      setResolutionReason("");
      dispatch(fetchHeldPropertyCommissions({ page: heldMeta.current_page || 1, status: heldFilter }));
    } catch (err) {
      toast.error(err || "Could not resolve held commission");
    } finally {
      setResolving(false);
    }
  };

  const handleRecordRefund = async ({ reason, refund_reference }) => {
    if (!refundSnapshot?.payment_id || refundSaving) return false;
    try {
      setRefundSaving(true);
      await dispatch(recordCommissionRefund({
        paymentId: refundSnapshot.payment_id,
        reason,
        refund_reference,
      })).unwrap();
      toast.success("Full refund recorded. No money transfer was initiated.");
      setRefundSnapshot(null);
      dispatch(fetchHeldPropertyCommissions({ page: heldMeta.current_page || 1, status: heldFilter }));
      return true;
    } catch (err) {
      toast.error(err || "Could not record the full refund");
      return false;
    } finally {
      setRefundSaving(false);
    }
  };

  return (
    <>
      <div className="layout-container">
        <MainLayout user={user} onLogout={onLogout} />
        <div className="main-content">
          <div className="container-fluid mt-4 mb-5 pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="text-secondary fw-bold">Property Commissions</h2>
              <div className="d-flex gap-2">
              <button className="btn btn-outline-primary shadow-sm" disabled={!canEdit} title={superadminTitle} onClick={() => openAddForType("App\\Models\\GreenlandCommissionDefault")}>
                <i className="bi bi-plus-circle me-2"></i> Add Greenland Defaults
              </button>
              <button className="btn btn-primary shadow-sm" disabled={!canEdit} title={superadminTitle} onClick={openAddModal}>
                <i className="bi bi-plus-circle me-2"></i> Add Commission Rule
              </button>
              </div>
            </div>
            {!canEdit && (
              <div className="alert alert-warning" role="status">
                {SUPERADMIN_ONLY_LABEL}: you can view commission rates and held commissions, but only a Superadmin can change rates, resolve held commissions or record refunds.
              </div>
            )}
            <div className="alert alert-info" role="status">
              Unity catalog mapping is not configured yet. Positive commission pools are recorded as awaiting mapping, and no recipient distribution is sent until the catalog mapping task is completed.
            </div>

            <div className="table-responsive shadow-sm rounded bg-white p-3">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>S/L</th>
                    <th>Applies To</th>
                    <th>Target Name</th>
                    <th>Payment Type</th>
                    <th>Commission Pool Rate</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions && commissions.length > 0 ? (
                    commissions.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>{item.commissionable_type === "App\\Models\\Property" ? "Property" : item.commissionable_type === "App\\Models\\GreenlandCommissionDefault" ? "Greenland Default" : "Property Type"}</td>
                        <td>{getTargetName(item.commissionable_type, item.commissionable_id)}</td>
                        <td>{formatPaymentType(item.payment_type)}</td>
                        <td><span className="badge bg-info text-dark">{item.commission_type === "percentage" ? `${item.commission_value}%` : `Legacy fixed amount: ৳${item.commission_value}`}</span></td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => openEditModal(item)}
                            disabled={!canEdit}
                            title={!canEdit ? SUPERADMIN_ONLY_LABEL : item.commission_type === "percentage" ? "Edit" : "Replace legacy fixed amount with an approved percentage"}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteClick(item)}
                            disabled={!canEdit}
                            title={canEdit ? "Delete" : SUPERADMIN_ONLY_LABEL}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        {status === "loading"
                          ? "Loading..."
                          : "No commission rules found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  {isEditing ? "Edit Commission Rule" : "Add Commission Rule"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form onSubmit={handleSubmit}>
                  {!isEditing && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Commission On *</label>
                        <select
                          className="form-select"
                          name="commissionable_type"
                          value={formData.commissionable_type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="App\Models\Property">Property Override</option>
                          <option value="App\Models\PropertyType">Property Type Override</option>
                          <option value="App\Models\GreenlandCommissionDefault">Greenland-wide Default</option>
                        </select>
                      </div>
                      
                      {formData.commissionable_type !== "App\\Models\\GreenlandCommissionDefault" && <div className="mb-3">
                        <label className="form-label">
                          Select {formData.commissionable_type === "App\\Models\\Property" ? "Property" : "Property Type"} *
                        </label>
                        <select
                          className="form-select"
                          name="commissionable_id"
                          value={formData.commissionable_id}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select target...</option>
                          {formData.commissionable_type === "App\\Models\\Property" 
                            ? properties.map((prop) => (
                                <option key={prop.id} value={prop.id}>{prop.title || prop.name}</option>
                              ))
                            : propertyTypes.map((pt) => (
                                <option key={pt.id} value={pt.id}>{pt.name || pt.title}</option>
                              ))
                          }
                        </select>
                      </div>}

                      <div className="mb-3">
                        <label className="form-label">Payment Type *</label>
                        <select
                          className="form-select"
                          name="payment_type"
                          value={formData.payment_type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Payment Type</option>
                          <option value="booking_fee">Booking Fee</option>
                          <option value="down_payment">Down Payment</option>
                          <option value="installment">Installment</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  {isEditing && (
                    <div className="alert alert-info">
                      Updating rule for <strong>{formatPaymentType(formData.payment_type)}</strong> on <strong>{getTargetName(formData.commissionable_type, formData.commissionable_id)}</strong>.
                      {selectedItem?.commission_type !== "percentage" && <div className="mt-2">This legacy fixed amount will be replaced with a percentage. Existing confirmed payment commission snapshots will remain unchanged.</div>}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Commission Pool Percentage *</label>
                    <div className="form-text mb-1">This percentage of confirmed money received forms the pool Unity distributes among recipients. Enter 0 to intentionally disable this stage.</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Commission Percentage *</label>
                    <input
                      type="number"
                      className="form-control"
                      name="commission_value"
                      value={formData.commission_value}
                      onChange={handleInputChange}
                      placeholder="Enter percentage (0-100)"
                      min="0"
                      step="0.01"
                      max="100"
                      aria-label="Commission pool percentage"
                      required
                    />
                  </div>
                </form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                  {isEditing ? "Update" : "Save"}
                </Button>
              </Modal.Footer>
            </Modal>

            <div className="card shadow-sm mt-4">
              <div className="card-body">
                <h5 className="card-title">Effective rates and missing configuration</h5>
                <p className="text-muted">Preview the rate selected for each stage. Property overrides take priority over property type, then Greenland-wide defaults. A missing rate holds the commission for resolution; an explicit 0% is configured.</p>
                <div className="row g-2 align-items-end">
                  <div className="col-md-5">
                    <label className="form-label">Preview property</label>
                    <select className="form-select" value={previewPropertyId} onChange={(e) => setPreviewPropertyId(e.target.value)}>
                      <option value="">Select a property</option>
                      {properties.map((property) => <option key={property.id} value={property.id}>{property.title || property.name}</option>)}
                    </select>
                  </div>
                </div>
                {previewPropertyId && effectiveStatus === "loading" && <p className="mt-3 mb-0">Loading effective rates…</p>}
                {previewPropertyId && effectiveStatus === "succeeded" && <div className="table-responsive mt-3"><table className="table table-sm table-bordered mb-0"><thead><tr><th>Payment stage</th><th>Effective rate</th><th>Source</th></tr></thead><tbody>{["booking_fee", "down_payment", "installment"].map((stage) => { const rate = effective?.rates?.[stage]; return <tr key={stage}><td>{formatPaymentType(stage)}</td><td>{rate?.configured ? `${rate.commission_value}%` : <span className="badge bg-warning text-dark">Missing — commission held</span>}</td><td>{rate?.configured ? formatSource(rate.source) : "Resolve configuration before payout"}</td></tr>; })}</tbody></table></div>}
              </div>
            </div>

            <div className="card shadow-sm mt-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center"><h5 className="card-title mb-0">Commission status</h5><Button size="sm" variant="outline-secondary" onClick={() => dispatch(fetchHeldPropertyCommissions({ page: heldMeta.current_page || 1, status: heldFilter }))}>Refresh</Button></div>
                <p className="text-muted mt-2">Payments remain recorded while commission is held. Resolving a hold applies only to that payment snapshot and does not change current rates.</p>
                <div className="mb-3"><label className="form-label">Status</label><select className="form-select" value={heldFilter} onChange={(e) => { setHeldFilter(e.target.value); dispatch(fetchHeldPropertyCommissions({ page: 1, status: e.target.value })); }}><option value="held">Held — rate needs resolution</option><option value="awaiting_mapping">Awaiting Unity catalog mapping</option><option value="complete">Complete</option><option value="reversal_pending">Refund reversal pending</option><option value="reversed">Reversed</option><option value="all">All statuses</option></select></div>
                {heldStatus === "loading" && <p>Loading…</p>}
                {heldStatus !== "loading" && !held?.length && <p className="text-muted mb-0">No {heldFilter === "all" ? "commission snapshots" : `${heldFilter.replaceAll("_", " ")} commissions`}.</p>}
                {!!held?.length && <div className="table-responsive"><table className="table table-sm align-middle"><thead><tr><th>Payment</th><th>Property / Booking</th><th>Received</th><th>Confirmed</th><th>Status / Reason</th><th>Actions</th></tr></thead><tbody>{held.map((item) => <tr key={item.id}><td>{formatPaymentType(item.payment_type || item.stage)}</td><td>{item.property?.name || item.booking?.property?.name || item.booking?.property?.title || `Booking #${item.booking_id || item.booking?.id || "—"}`}</td><td>৳{item.source_amount}</td><td>{item.confirmed_at ? new Date(item.confirmed_at).toLocaleString() : "—"}</td><td><span className={`badge ${item.status === "held" ? "bg-warning text-dark" : item.status === "reversed" ? "bg-secondary" : "bg-info text-dark"}`}>{item.status}</span>{item.hold_reason && <div className="small text-muted">{item.hold_reason}</div>}</td><td className="d-flex gap-2">{item.status === "held" && <Button size="sm" variant="warning" disabled={!canEdit} title={superadminTitle} onClick={() => { setResolution(item); setResolutionRate(""); setResolutionReason(""); }}>Resolve rate</Button>}{canRecordCommissionRefund(item) && <Button size="sm" variant="outline-danger" disabled={!canEdit} title={superadminTitle} onClick={() => setRefundSnapshot(item)}>Record full refund</Button>}</td></tr>)}</tbody></table></div>}
                {Number(heldMeta?.last_page) > 1 && <div className="d-flex justify-content-between align-items-center mt-2"><span className="small text-muted">Page {heldMeta.current_page} of {heldMeta.last_page}</span><div className="btn-group btn-group-sm"><Button variant="outline-secondary" disabled={heldMeta.current_page <= 1 || heldStatus === "loading"} onClick={() => dispatch(fetchHeldPropertyCommissions({ page: heldMeta.current_page - 1, status: heldFilter }))}>Previous</Button><Button variant="outline-secondary" disabled={heldMeta.current_page >= heldMeta.last_page || heldStatus === "loading"} onClick={() => dispatch(fetchHeldPropertyCommissions({ page: heldMeta.current_page + 1, status: heldFilter }))}>Next</Button></div></div>}
              </div>
            </div>

            <Modal show={!!resolution} onHide={() => setResolution(null)} centered>
              <Modal.Header closeButton><Modal.Title>Resolve held commission</Modal.Title></Modal.Header>
              <form onSubmit={handleResolve}><Modal.Body><p>This rate applies only to the selected payment, using its original confirmation time.</p><label className="form-label">Commission percentage</label><input className="form-control mb-3" type="number" min="0" max="100" step="0.01" value={resolutionRate} onChange={(e) => setResolutionRate(e.target.value)} required /><label className="form-label">Reason</label><textarea className="form-control" value={resolutionReason} onChange={(e) => setResolutionReason(e.target.value)} required /></Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setResolution(null)} disabled={resolving}>Cancel</Button><Button type="submit" variant="primary" disabled={resolving}>{resolving ? "Resolving…" : "Resolve payment"}</Button></Modal.Footer></form>
            </Modal>

            <CommissionRefundModal
              show={!!refundSnapshot}
              snapshot={refundSnapshot}
              saving={refundSaving}
              onHide={() => setRefundSnapshot(null)}
              onSubmit={handleRecordRefund}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteModal && itemToDelete && (
              <DeleteConfirmationModal
                show={showDeleteModal}
                onClose={() => {
                  setShowDeleteModal(false);
                  setItemToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                title="Delete Commission Rule"
                message={`Are you sure you want to delete this commission rule for "${formatPaymentType(itemToDelete.payment_type)}"? This action cannot be undone.`}
              />
            )}

            {/* Success Modal */}
            <SuccessModal
              show={showSuccessModal}
              onClose={() => setShowSuccessModal(false)}
              message={successMessage}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyCommissionPage;
